import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { HelperService } from '@app/services/helper.service';
import { MemberPaths } from 'src/app/constants/routes';
import { Member } from 'src/app/models/member';

class ProfileDisplayField {
    label: string;
    jsonKey: string;
    value: string | number | null | undefined;
    visible: boolean;
}

@Component({
    selector: 'mwp-profile-info',
    templateUrl: 'profile-info.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class ProfileInfoComponent implements OnInit {
    @Input() public member: Member;

    public displayEditProfileButton: boolean;

    constructor(
        private router: Router,
        private helperService: HelperService
    ) {
    }

    ngOnInit() {
        this.buildProfileFields();
        this.displayEditProfileButton = this.member.enableSaveButton
    }

    profileFields: ProfileDisplayField[] = [];

    private buildProfileFields(): void {
        const member = this.member;

        this.profileFields = [
            {
                label: 'SID',
                jsonKey: 'sid',
                value: member.number,
                visible: member.showNumber,
            },
            {
                label: 'ERID',
                jsonKey: 'erid',
                value: member.erid,
                visible: member.showERID,
            },
            {
                label: 'Registered Email',
                jsonKey: 'registeredEmail',
                value: member.enrollmentUID,
                visible: member.showEnrollmentUID,
            },
            {
                label: 'First Name',
                jsonKey: 'firstName',
                value: member.firstName,
                visible: member.showName,
            },
            {
                label: 'Last Name',
                jsonKey: 'lastName',
                value: member.lastName,
                visible: member.showName,
            },
            {
                label: 'SSN',
                jsonKey: 'ssn',
                value: member.ssnDecrypted,
                visible: member.showSSNDecrypted,
            },
            {
                label: 'Date Of Birth',
                jsonKey: 'dateOfBirth',
                value: this.helperService.formatDateString(member.dobDecrypted),
                visible: member.showDOBDecrypted,
            },
            {
                label: 'Gender',
                jsonKey: 'gender',
                value: member.gender,
                visible: member.showGender,
            },
            {
                label: 'Home Email',
                jsonKey: 'homeEmail',
                value: member.homeEmail,
                visible: member.showHomeEmail,
            },
            {
                label: 'Home Phone',
                jsonKey: 'homePhone',
                value: member.homePhone,
                visible: member.showHomePhone,
            },
            {
                label: 'Cell Phone',
                jsonKey: 'cellPhone',
                value: member.cellPhone,
                visible: member.showCellPhone,
            },
            {
                label: 'Address 1',
                jsonKey: 'address1',
                value: member.homeAddressLine1,
                visible: member.showAddress,
            },
            {
                label: 'Address 2',
                jsonKey: 'address2',
                value: member.homeAddressLine2,
                visible: member.showAddress,
            },
            {
                label: 'City',
                jsonKey: 'city',
                value: member.homeCity,
                visible: member.showHomeCity,
            },
            {
                label: 'Home State',
                jsonKey: 'homeState',
                value: member.homeState,
                visible: member.showHomeState,
            },
            {
                label: 'Zip Code',
                jsonKey: 'zipCode',
                value: member.homeZipCode,
                visible: member.showHomeZipCode,
            },
            {
                label: 'Date of Hire',
                jsonKey: 'dateOfHire',
                value: this.helperService.formatDateString(member.dateOfHire),
                visible: member.showDateOfHire,

            },
            {
                label: 'Job Title',
                jsonKey: 'jobTitle',
                value: member.workJobTitle,
                visible: member.showWorkJobTitle,
            },
            {
                label: 'Work Hours',
                jsonKey: 'workHours',
                value: member.workHours,
                visible: member.showWorkHours,
            },
            {
                label: member.companyLocationCaption,
                jsonKey: 'companyLocation',
                value: member.companyLocation,
                visible: member.showCompanyLocation,
            },
            {
                label: member.employmentStatusCaption,
                jsonKey: 'employmentStatus',
                value: member.employmentStatus,
                visible: member.showEmploymentStatus,
            },
            {
                label: member.departmentCaption,
                jsonKey: 'department',
                value: member.department,
                visible: member.showDepartment,
            },
            {
                label: member.positionCaption,
                jsonKey: 'position',
                value: member.position,
                visible: member.showPosition,
            },
            {
                label: member.displayDivisionCaption,
                jsonKey: 'division',
                value: member.displayDivision,
                visible: member.showDivision,
            },
            {
                label: member.displayEEClass1Caption,
                jsonKey: 'employeeClass1',
                value: member.displayEEClass1,
                visible: member.showEEClass1,
            },
            {
                label: member.displayEEClass2Caption,
                jsonKey: 'employeeClass2',
                value: member.displayEEClass2,
                visible: member.showEEClass2,
            },
            {
                label: member.displayEEClass3Caption,
                jsonKey: 'employeeClass3',
                value: member.displayEEClass3,
                visible: member.showEEClass3,
            },
            {
                label: member.displayEEClass4Caption,
                jsonKey: 'employeeClass4',
                value: member.displayEEClass4,
                visible: member.showEEClass4,
            },
            {
                label: member.displayEEClass5Caption,
                jsonKey: 'employeeClass5',
                value: member.displayEEClass5,
                visible: member.showEEClass5,
            },
            {
                label: member.displayUDF1Caption,
                jsonKey: 'udf1',
                value: member.displayUDF1,
                visible: member.showUdf1,
            },
            {
                label: member.displayUDF2Caption,
                jsonKey: 'udf2',
                value: member.displayUDF2,
                visible: member.showUdf2,
            },
            {
                label: member.displayUDF3Caption,
                jsonKey: 'udf3',
                value: member.displayUDF3,
                visible: member.showUdf3,
            },
            {
                label: member.displayUDF4Caption,
                jsonKey: 'udf4',
                value: member.displayUDF4,
                visible: member.showUdf4,
            },
            {
                label: member.displayUDF5Caption,
                jsonKey: 'udf5',
                value: member.displayUDF5,
                visible: member.showUdf5,
            },
        ];
    }

    public goToProfile(): void {
        this.router.navigateByUrl(MemberPaths.Profile);
    }
}